/**
 * SAIF INSTRUCTOR DASHBOARD
 *
 * Surfaces every student-facing feature on the site:
 *   - Practice recordings with pitch-graph replay + score breakdown
 *   - Per-student streak heatmap, badges, completed lessons
 *   - Filterable recording feed (lesson, score, with/without feedback)
 *   - Best-take pinning per lesson
 *   - Bookings (upcoming, reschedule requests, last 7d) with one-click jump
 *     to the booked student's recent practice
 *   - Cross-student "Recent activity" feed of recordings still awaiting feedback
 *
 * Backend auth: every read/write goes through the worker, which re-checks the
 * caller's email against INSTRUCTOR_EMAILS before returning anything. The
 * browser-side allowlist is UX only.
 */
(function () {
  'use strict';

  var listEl       = document.getElementById('SaifInstructorList');
  var statusEl     = document.getElementById('SaifInstructorListStatus');
  var detailEl     = document.getElementById('SaifInstructorDetail');
  var searchEl     = document.getElementById('SaifInstructorSearch');
  var sortEl       = document.getElementById('SaifInstructorSort');
  var overviewEl   = document.getElementById('SaifInstructorOverview');
  var loadMoreEl   = document.getElementById('SaifInstructorLoadMore');
  if (!listEl || !detailEl) return;

  if (!window.SaifSync || !window.SaifSync.ready) {
    if (statusEl) statusEl.textContent = 'Saif Sync is not configured. Sign in and ensure the App Proxy is reachable.';
    return;
  }

  /* -------------------------------------------------- state */
  var allStudents = [];
  var allBookings = [];
  var bookingIssues = [];
  var studentsCursor = null; // opaque worker cursor for the next students page
  var selectedId = null;
  var detailCache = {};
  var bookingsLoaded = false;
  var feedLoaded = false;
  var bookingFilter = 'upcoming';

  // Filter state for the recording panel — per-student so switching back keeps it
  var recordingFilters = {}; // { customerId: { lesson, score, withFeedback } }

  var QUICK_REPLIES = [
    'Strong recitation — keep it up.',
    'Keep your sustain steady on the long notes.',
    'Tonic intonation slightly flat — settle on the qarar.',
    'Lovely warmth in your voice there.',
    'Push more breath support on the long phrases.',
    'Maqam transitions are clean — well done.'
  ];

  var listFilter = 'all';

  /* -------------------------------------------------- list rendering */
  function renderList() {
    var query = (searchEl && searchEl.value || '').toLowerCase().trim();
    var sortBy = (sortEl && sortEl.value) || 'needs_feedback';
    var rows = allStudents.slice();

    if (listFilter === 'needs') rows = rows.filter(function (s) { return (s.pendingFeedback || 0) > 0; });
    if (listFilter === 'idle')  rows = rows.filter(function (s) { var r = atRiskLevel(s); return r === 'risk' || r === 'cool'; });

    if (query) {
      rows = rows.filter(function (s) {
        return (s.name && s.name.toLowerCase().indexOf(query) !== -1) ||
               (s.email && s.email.toLowerCase().indexOf(query) !== -1);
      });
    }

    rows.sort(function (a, b) {
      if (sortBy === 'needs_feedback') {
        var d = (b.pendingFeedback || 0) - (a.pendingFeedback || 0);
        if (d !== 0) return d;
        return (b.latestRecordingAt || '').localeCompare(a.latestRecordingAt || '');
      }
      if (sortBy === 'streak')     return (b.streak || 0) - (a.streak || 0);
      if (sortBy === 'recordings') return (b.recordingCount || 0) - (a.recordingCount || 0);
      if (sortBy === 'best_score') return (b.bestScore || 0) - (a.bestScore || 0);
      if (sortBy === 'alpha')      return (a.name || '').localeCompare(b.name || '');
      // recent
      var ax = a.latestRecordingAt || a.lastActiveAt || '';
      var bx = b.latestRecordingAt || b.lastActiveAt || '';
      return bx.localeCompare(ax);
    });

    listEl.innerHTML = rows.map(function (s) {
      var initial = (s.name || s.email || '?').trim().charAt(0).toUpperCase();
      var lastWhen = s.latestRecordingAt || s.lastActiveAt;
      var lastLabel = lastWhen ? relativeTime(lastWhen) : 'no activity yet';
      var idle = atRiskLevel(s);

      var meta = [];
      if (s.recordingCount) {
        // This is a REVIEW dashboard: the count that matters is how many takes
        // the student actually SUBMITTED. Showing the raw total made a student
        // who practised but never submitted read as e.g. "7 recs" and then open
        // to an empty review list. Show submitted, with the total for context
        // when they differ ("0/7 submitted").
        var subN = s.submittedCount || 0;
        meta.push(subN === s.recordingCount
          ? subN + ' rec' + (subN === 1 ? '' : 's')
          : subN + '/' + s.recordingCount + ' submitted');
      }
      if (s.streak)         meta.push('🔥 ' + s.streak + 'd');

      var badges = '';
      if (s.pendingFeedback)   badges += '<span class="saif-instructor__list-badge saif-instructor__list-badge--gold" title="Recordings without your feedback">' + s.pendingFeedback + ' new</span>';
      if (s.upcomingBookings)  badges += '<span class="saif-instructor__list-badge" title="Upcoming bookings">📅 ' + s.upcomingBookings + '</span>';
      if (idle === 'risk')     badges += '<span class="saif-instructor__list-badge saif-instructor__list-badge--warn" title="No activity in 14+ days">idle</span>';
      else if (idle === 'cool') badges += '<span class="saif-instructor__list-badge saif-instructor__list-badge--cool" title="No activity in 7+ days">cooling</span>';

      var selected = String(s.customerId) === String(selectedId);
      var isSelected = selected ? ' saif-instructor__list-item--active' : '';
      return '<li class="saif-instructor__list-item' + isSelected + '" data-customer-id="' + esc(s.customerId) + '" role="option" aria-selected="' + (selected ? 'true' : 'false') + '" tabindex="0">' +
        '<span class="saif-instructor__avatar" aria-hidden="true">' + esc(initial) + '</span>' +
        '<span class="saif-instructor__list-text">' +
          '<span class="saif-instructor__list-name">' + esc(s.name || s.email) + '</span>' +
          '<span class="saif-instructor__list-meta">' + esc(meta.join(' · ')) + '</span>' +
          (badges ? '<span class="saif-instructor__list-badges">' + badges + '</span>' : '') +
        '</span>' +
        '<span class="saif-instructor__list-when">' + esc(lastLabel) + '</span>' +
      '</li>';
    }).join('');

    if (!rows.length) {
      listEl.innerHTML = '<li role="option" aria-disabled="true" class="saif-instructor__list-empty">No students match your search.</li>';
    }
  }

  function atRiskLevel(s) {
    var ts = s.latestRecordingAt || s.lastActiveAt;
    if (!ts) return 'never';
    var days = (Date.now() - new Date(ts).getTime()) / 86400000;
    if (days > 14) return 'risk';
    if (days > 7)  return 'cool';
    return 'ok';
  }

  function renderOverview() {
    if (!overviewEl) return;
    var nowMs = Date.now();
    var weekAgo = nowMs - 7 * 86400000;
    var students = 0, recordingsThisWeek = 0, pending = 0, bookings = 0, atRisk = 0;
    allStudents.forEach(function (s) {
      students++;
      if (s.latestRecordingAt && new Date(s.latestRecordingAt).getTime() > weekAgo) recordingsThisWeek++;
      pending  += (s.pendingFeedback || 0);
      bookings += (s.upcomingBookings || 0);
      if (atRiskLevel(s) === 'risk') atRisk++;
    });
    setStat('students',  students);
    setStat('recordings', recordingsThisWeek);
    setStat('pending',   pending);
    setStat('bookings', bookingsLoaded ? allBookings.filter(function(b){return b.status!=='cancelled' && new Date(b.end || b.start).getTime()>nowMs;}).length : bookings);
    setStat('atrisk',    atRisk);
  }
  function setStat(name, val) {
    var node = overviewEl.querySelector('[data-stat="' + name + '"] [data-value]');
    if (node) node.textContent = val;
  }

  // The worker paginates /instructor/students with `?cursor=` and returns
  // `nextCursor`/`hasMore` (SaifSync.instructor.listStudents sends the legacy
  // `pageInfo` param name, which the worker ignores), so call the App Proxy
  // route directly with the param the worker actually reads.
  function fetchStudentsPage(cursor) {
    var base = (window.SaifSync.config.proxyPath || '').replace(/\/$/, '');
    var qs = '?pageSize=100' + (cursor ? '&cursor=' + encodeURIComponent(cursor) : '');
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 20000);
    var init = { method: 'GET', headers: { 'Accept': 'application/json' }, credentials: 'same-origin' };
    if (ctrl) init.signal = ctrl.signal;
    return fetch(base + '/instructor/students' + qs, init)
      .then(function (resp) {
        clearTimeout(timer);
        if (!resp.ok) return { ok: false, error: 'HTTP_' + resp.status };
        return resp.json();
      })
      .catch(function (err) {
        clearTimeout(timer);
        return { ok: false, error: (err && err.name === 'AbortError') ? 'TIMEOUT' : 'NETWORK_ERROR' };
      });
  }

  function loadList() {
    if (statusEl) statusEl.textContent = 'Loading students...';
    if (loadMoreEl) loadMoreEl.hidden = true;
    allStudents = [];
    studentsCursor = null;
    loadStudentsPage(null);
  }

  function loadStudentsPage(cursor) {
    fetchStudentsPage(cursor).then(function (r) {
      if (loadMoreEl) {
        loadMoreEl.disabled = false;
        loadMoreEl.textContent = 'Load more students';
      }
      if (!r || !r.ok) {
        if (statusEl) statusEl.textContent = 'Could not load students (' + (r && r.error || 'unknown') + ')';
        if (loadMoreEl) loadMoreEl.hidden = !studentsCursor;
        return;
      }
      // Dedupe across pages — UPDATED_AT ordering can shift between fetches
      var seen = {};
      allStudents.forEach(function (s) { seen[String(s.customerId)] = true; });
      (r.students || []).forEach(function (s) {
        if (!seen[String(s.customerId)]) allStudents.push(s);
      });
      studentsCursor = r.nextCursor || null;
      if (statusEl) {
        statusEl.textContent = allStudents.length
          ? allStudents.length + ' student' + (allStudents.length === 1 ? '' : 's') + ' with practice data' + (studentsCursor ? ' (more available)' : '')
          : 'No students have practice data yet.';
      }
      if (loadMoreEl) loadMoreEl.hidden = !studentsCursor;
      renderList();
      renderOverview();
      // The Review-queue tab may have been opened before this first page
      // arrived, latching a false "caught up" on an empty roster — refresh
      // it now that allStudents is populated.
      var feedTabEl = document.querySelector('.saif-instructor__tab[data-tab="feed"]');
      if (feedTabEl && feedTabEl.classList.contains('saif-instructor__tab--active')) {
        loadFeed();
        if (allStudents.length) feedLoaded = true;
      }
    });
  }

  if (loadMoreEl) {
    loadMoreEl.addEventListener('click', function () {
      if (!studentsCursor) return;
      loadMoreEl.disabled = true;
      loadMoreEl.textContent = 'Loading...';
      loadStudentsPage(studentsCursor);
    });
  }

  /* -------------------------------------------------- detail rendering */
  // On phones the list is capped at 40vh and the detail pane stacks below it;
  // bring the pane into view on selection so the tap visibly does something.
  function scrollDetailIntoView() {
    if (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) {
      detailEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function loadDetail(customerId, force) {
    selectedId = customerId;
    renderList();
    if (!force && detailCache[customerId]) {
      renderDetail(detailCache[customerId]);
      scrollDetailIntoView();
      return;
    }
    detailEl.innerHTML = '<div class="saif-instructor__loading"><div class="saif-instructor__spinner"></div><p>Loading student...</p></div>';
    if (!force) scrollDetailIntoView();
    window.SaifSync.instructor.getStudent(customerId).then(function (r) {
      if (!r || !r.ok) {
        detailEl.innerHTML = '<p class="saif-instructor__error">Could not load student (' + esc(r && r.error || 'unknown') + ').</p>';
        return;
      }
      detailCache[customerId] = r.student;
      renderDetail(r.student);
    });
  }

  function renderDetail(student) {
    if (!student) return;
    var recordings = student.recordings || [];
    var progress   = student.progress   || {};
    var badges     = student.badges     || [];
    var streak     = (progress.streak && progress.streak.count) || 0;
    var longest    = (progress.streak && progress.streak.longest) || 0;
    var completedList = progress.completed || [];

    var heatmap = renderHeatmap(progress.lastSeen || {});

    // Payment/access badge — subscriber vs course months vs trial vs lapsed
    var studio = student.studio || {};
    var accessBadge = '';
    (function () {
      var until = studio.until
        ? new Date(studio.until).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
      var map = {
        subscriber: ['Subscriber', 'sub'],
        course: ['Course access' + (until ? ' · until ' + until : ''), 'course'],
        trial: ['Trial' + (until ? ' · ends ' + until : ''), 'trial'],
        lapsed: ['Access lapsed', 'lapsed'],
        free: ['Free tier', 'free']
      };
      var m = map[studio.status];
      if (m) accessBadge = '<span class="saif-instructor__access saif-instructor__access--' + m[1] + '">' + m[0] + '</span>';
    })();

    var initial = (student.name || student.email || '?').trim().charAt(0).toUpperCase();
    var customerId = student.customerId;

    // Filters for this student
    if (!recordingFilters[customerId]) {
      recordingFilters[customerId] = { lesson: 'all', score: 'all', withFeedback: 'all' };
    }
    var filters = recordingFilters[customerId];

    detailEl.innerHTML =
      '<header class="saif-instructor__student-header">' +
        '<div class="saif-instructor__student-id">' +
          '<span class="saif-instructor__student-avatar" aria-hidden="true">' + esc(initial) + '</span>' +
          '<div>' +
            '<h2 class="saif-instructor__student-name">' + esc(student.name || student.email) + '</h2>' +
            '<p class="saif-instructor__student-email">' + esc(student.email) + '</p>' +
            accessBadge +
          '</div>' +
        '</div>' +
        '<dl class="saif-instructor__stats">' +
          '<div><dt>Streak</dt><dd>' + streak + '<span>d</span></dd></div>' +
          '<div><dt>Longest</dt><dd>' + longest + '<span>d</span></dd></div>' +
          '<div><dt>Lessons done</dt><dd>' + completedList.length + '</dd></div>' +
          '<div><dt>Submitted</dt><dd>' + recordings.length + '</dd></div>' +
          '<div><dt>Reviews left</dt><dd>' + (student.reviewCredits || 0) + '</dd></div>' +
        '</dl>' +
      '</header>' +

      '<section class="saif-instructor__bio-grid">' +
        '<div class="saif-instructor__bio-card">' +
          '<h3 class="saif-instructor__bio-heading">30-day activity</h3>' +
          heatmap +
        '</div>' +
        (badges.length
          ? '<div class="saif-instructor__bio-card">' +
              '<h3 class="saif-instructor__bio-heading">Badges</h3>' +
              '<div class="saif-instructor__badges">' +
                badges.map(function (b) { return '<span class="saif-instructor__badge">' + esc(badgeLabel(b)) + '</span>'; }).join('') +
              '</div>' +
            '</div>'
          : '') +
        (completedList.length
          ? '<div class="saif-instructor__bio-card">' +
              '<h3 class="saif-instructor__bio-heading">Completed lessons</h3>' +
              '<ul class="saif-instructor__lesson-list">' +
                completedList.map(function (l) { return '<li>' + esc(humanizeLessonId(l)) + '</li>'; }).join('') +
              '</ul>' +
            '</div>'
          : '') +
        // Student-notes card removed — the lesson-page notes feature was retired (July 2026).
      '</section>' +

      renderRecordingFilters(recordings, filters) +
      renderRecordingsBlock(recordings, filters, customerId);

    wireDetailEvents(student);
    paintAllPitchGraphs(detailEl);
  }

  /* ---- heatmap ---- */
  function renderHeatmap(lastSeen) {
    // Count practice events per day across all lesson keys
    var counts = {};
    Object.keys(lastSeen).forEach(function (k) {
      var d = new Date(lastSeen[k]);
      if (isNaN(d.getTime())) return;
      var key = d.toISOString().slice(0, 10);
      counts[key] = (counts[key] || 0) + 1;
    });
    var cells = [];
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var total = 0;
    for (var i = 29; i >= 0; i--) {
      var d = new Date(today);
      d.setDate(today.getDate() - i);
      var key = d.toISOString().slice(0, 10);
      var n = counts[key] || 0;
      total += n;
      var level = n === 0 ? 0 : n === 1 ? 1 : n < 4 ? 2 : 3;
      var label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      var text = label + (n ? ' · ' + n + ' touch' + (n === 1 ? '' : 'es') : '');
      // Non-empty cells are focusable with an aria-label mirroring the
      // tooltip; empty cells stay decorative (title-only, hidden from AT).
      cells.push('<span class="saif-heatmap__cell" data-level="' + level + '" title="' + esc(text) + '"' +
        (n ? ' tabindex="0" aria-label="' + esc(text) + '"' : ' aria-hidden="true"') + '></span>');
    }
    return '<div class="saif-heatmap">' + cells.join('') + '</div>' +
      '<p class="visually-hidden">' + total + ' practice touch' + (total === 1 ? '' : 'es') + ' in the last 30 days</p>';
  }

  /* ---- recording filters ---- */
  function renderRecordingFilters(recordings, filters) {
    var lessonOptions = uniqueLessonOptions(recordings);
    return '<section class="saif-instructor__rec-toolbar">' +
      '<div class="saif-instructor__rec-toolbar-row">' +
        '<label>Lesson <select data-rec-filter="lesson">' +
          '<option value="all"' + (filters.lesson === 'all' ? ' selected' : '') + '>All</option>' +
          lessonOptions.map(function (opt) {
            return '<option value="' + esc(opt.value) + '"' + (filters.lesson === opt.value ? ' selected' : '') + '>' + esc(opt.label) + '</option>';
          }).join('') +
        '</select></label>' +
        '<label>Score <select data-rec-filter="score">' +
          ['all','9+','7+','below7','none'].map(function (v) {
            var label = v === 'all' ? 'All' : v === 'none' ? 'No score' : v === 'below7' ? 'Below 7' : v + '/10';
            return '<option value="' + v + '"' + (filters.score === v ? ' selected' : '') + '>' + label + '</option>';
          }).join('') +
        '</select></label>' +
        '<label>Feedback <select data-rec-filter="withFeedback">' +
          ['all','none','given'].map(function (v) {
            var label = v === 'all' ? 'All' : v === 'none' ? 'Awaiting feedback' : 'Already given';
            return '<option value="' + v + '"' + (filters.withFeedback === v ? ' selected' : '') + '>' + label + '</option>';
          }).join('') +
        '</select></label>' +
      '</div>' +
    '</section>';
  }

  function uniqueLessonOptions(recordings) {
    var seen = {};
    var out = [];
    recordings.forEach(function (r) {
      var key = r.lessonId || '__free';
      if (seen[key]) return;
      seen[key] = true;
      out.push({ value: key, label: humanizeLessonId(key) });
    });
    return out;
  }

  function applyFilters(recordings, filters) {
    return recordings.filter(function (r) {
      // Defensive: the worker already returns only submitted recordings, but
      // never surface a private practice take if one slips through.
      if (r.submittedForFeedback === false) return false;
      if (filters.lesson !== 'all') {
        var key = r.lessonId || '__free';
        if (key !== filters.lesson) return false;
      }
      var s = r.score;
      if (filters.score === '9+'    && !(typeof s === 'number' && s >= 9)) return false;
      if (filters.score === '7+'    && !(typeof s === 'number' && s >= 7)) return false;
      if (filters.score === 'below7'&& !(typeof s === 'number' && s <  7)) return false;
      if (filters.score === 'none'  &&  (typeof s === 'number')) return false;
      if (filters.withFeedback === 'none'  && r.feedback) return false;
      if (filters.withFeedback === 'given' && !r.feedback) return false;
      return true;
    });
  }

  function renderRecordingsBlock(recordings, filters, customerId) {
    var filtered = applyFilters(recordings, filters);
    if (!filtered.length) {
      // Distinguish "a filter hid them" from "nothing was ever submitted" —
      // the latter is the common case (the review list is submitted-only, so a
      // student who only practised privately lands here) and reads as a bug
      // unless we say why.
      var filtersActive = filters && (filters.lesson !== 'all' || filters.score !== 'all' || filters.withFeedback !== 'all');
      return filtersActive
        ? '<p class="saif-instructor__empty-recordings">No recordings match these filters.</p>'
        : '<p class="saif-instructor__empty-recordings">This student hasn’t submitted any recordings for a Personal Review yet. Private practice takes stay on their own account until they submit one.</p>';
    }

    // Best take per lesson — highest score, but only if there's a clear winner (score difference > 0)
    var bestByLesson = {};
    filtered.forEach(function (r) {
      if (typeof r.score !== 'number') return;
      var k = r.lessonId || '__free';
      if (!bestByLesson[k] || r.score > bestByLesson[k].score) bestByLesson[k] = r;
    });
    var bestIds = {};
    Object.keys(bestByLesson).forEach(function (k) { bestIds[bestByLesson[k].id] = true; });

    return '<div class="saif-instructor__recordings">' +
      filtered.map(function (rec) { return renderRecording(rec, bestIds[rec.id]); }).join('') +
    '</div>';
  }

  function wireDetailEvents(student) {
    var customerId = student.customerId;

    // Filter selects
    detailEl.querySelectorAll('[data-rec-filter]').forEach(function (sel) {
      sel.addEventListener('change', function () {
        var key = sel.getAttribute('data-rec-filter');
        recordingFilters[customerId][key] = sel.value;
        var recs = student.recordings || [];
        var block = detailEl.querySelector('.saif-instructor__recordings');
        var emptyMsg = detailEl.querySelector('.saif-instructor__empty-recordings');
        if (block) block.outerHTML = renderRecordingsBlock(recs, recordingFilters[customerId], customerId);
        else if (emptyMsg) emptyMsg.outerHTML = renderRecordingsBlock(recs, recordingFilters[customerId], customerId);
        wireFeedbackForms(student);
        paintAllPitchGraphs(detailEl);
      });
    });

    wireFeedbackForms(student);
  }

  // Instructor voice-note recorder — MediaRecorder with a hard 5-minute cap
  // (mirrors the student studio's own limit; the worker enforces 10 MB).
  var FB_VOICE_MAX_MS = 5 * 60 * 1000;
  function wireVoiceRecorder(form) {
    var recordBtn = form.querySelector('[data-voice-record]');
    var timerEl = form.querySelector('[data-voice-timer]');
    var preview = form.querySelector('[data-voice-preview]');
    var discardBtn = form.querySelector('[data-voice-discard]');
    if (!recordBtn || !window.MediaRecorder) { if (recordBtn) recordBtn.hidden = true; return; }
    var recorder = null, chunks = [], startedAt = 0, tick = null, stopTimer = null;

    function fmt(ms) {
      var s = Math.floor(ms / 1000);
      return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2);
    }
    function resetUi() {
      recordBtn.textContent = '🎙 Record voice note';
      recordBtn.classList.remove('is-recording');
      timerEl.hidden = true;
      if (tick) { clearInterval(tick); tick = null; }
      if (stopTimer) { clearTimeout(stopTimer); stopTimer = null; }
    }
    function discard() {
      form._voiceBlob = null;
      preview.hidden = true;
      preview.removeAttribute('src');
      discardBtn.hidden = true;
      resetUi();
    }
    discardBtn.addEventListener('click', discard);

    recordBtn.addEventListener('click', function () {
      if (recorder && recorder.state === 'recording') { recorder.stop(); return; }
      navigator.mediaDevices.getUserMedia({ audio: true }).then(function (stream) {
        var mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
          : (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : '');
        recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
        chunks = [];
        recorder.ondataavailable = function (e) { if (e.data && e.data.size) chunks.push(e.data); };
        recorder.onstop = function () {
          stream.getTracks().forEach(function (t) { t.stop(); });
          var blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
          form._voiceBlob = blob;
          preview.src = URL.createObjectURL(blob);
          preview.hidden = false;
          discardBtn.hidden = false;
          resetUi();
        };
        recorder.start();
        startedAt = Date.now();
        recordBtn.textContent = '■ Stop recording';
        recordBtn.classList.add('is-recording');
        timerEl.hidden = false;
        timerEl.textContent = '0:00';
        tick = setInterval(function () { timerEl.textContent = fmt(Date.now() - startedAt); }, 500);
        stopTimer = setTimeout(function () { if (recorder.state === 'recording') recorder.stop(); }, FB_VOICE_MAX_MS);
      }).catch(function () {
        recordBtn.textContent = 'Mic unavailable';
        recordBtn.disabled = true;
      });
    });
  }

  function wireFeedbackForms(student) {
    detailEl.querySelectorAll('[data-feedback-form]').forEach(function (form) {
      if (form._wired) return;
      form._wired = true;
      wireVoiceRecorder(form);
      var textarea = form.querySelector('textarea');
      // Quick-reply chips append to textarea
      form.querySelectorAll('[data-canned]').forEach(function (chip) {
        chip.addEventListener('click', function (e) {
          e.preventDefault();
          var sep = textarea.value.trim() ? '\n\n' : '';
          textarea.value = textarea.value + sep + chip.getAttribute('data-canned');
          textarea.focus();
        });
      });
      var quickBtn = form.querySelector('[data-quick-approve]');
      if (quickBtn) {
        quickBtn.addEventListener('click', function () {
          // One tap for takes that are simply good: clears the pending queue
          // and the student still gets the email + unread-feedback loop.
          textarea.value = "Reviewed your take — it's solid. Keep going exactly like this; nothing to correct here.";
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.dispatchEvent(new Event('submit', { cancelable: true }));
        });
      }
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var recordingId = form.getAttribute('data-rec-id');
        var customerId  = student.customerId;
        var btn = form.querySelector('button[type="submit"]');
        var msg = form.querySelector('[data-feedback-msg]');
        var text = (textarea.value || '').trim();
        var voiceBlob = form._voiceBlob || null;
        if (!text && !voiceBlob) {
          if (msg) msg.textContent = 'Type feedback or record a voice note first.';
          return;
        }
        if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }
        if (msg) msg.textContent = '';
        var send = function (audio) {
          return window.SaifSync.instructor.leaveFeedback(customerId, recordingId, text, audio);
        };
        var withAudio = voiceBlob
          ? new Promise(function (res, rej) {
              var fr = new FileReader();
              fr.onload = function () {
                var b64 = String(fr.result).split(',')[1] || '';
                res(send({ base64: b64, mimeType: voiceBlob.type || 'audio/webm' }));
              };
              fr.onerror = rej;
              fr.readAsDataURL(voiceBlob);
            })
          : send(null);
        withAudio.then(function (r) {
          if (btn) { btn.disabled = false; btn.textContent = 'Send feedback'; }
          if (r && r.ok) {
            if (msg) msg.textContent = 'Sent. The student will receive an email.';
            detailCache[customerId] = null;
            // Pull fresh list too — pendingFeedback count changed
            loadList();
            loadDetail(customerId, true);
          } else {
            if (msg) msg.textContent = 'Failed: ' + (r && r.error || 'unknown');
          }
        });
      });
    });
  }

  /* ---- recording card ---- */
  function renderRecording(rec, isBest) {
    var date = rec.createdAt ? new Date(rec.createdAt) : null;
    var dateLabel = date
      ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
        ' · ' + date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : '';
    var duration = (typeof rec.duration === 'number') ? Math.round(rec.duration) + 's' : '';
    var scoreNum = (typeof rec.score === 'number') ? rec.score : null;
    var score = scoreNum != null ? scoreNum.toFixed(1) + '/10' : '';
    var scoreClass = scoreNum == null ? '' :
      scoreNum >= 9 ? 'saif-instructor__rec-score--good' :
      scoreNum >= 7 ? 'saif-instructor__rec-score--ok'   :
                      'saif-instructor__rec-score--low';
    var clipLabel = rec.clipTitle ? rec.clipTitle : (rec.lessonId ? humanizeLessonId(rec.lessonId) : 'Free practice');
    var bestBadge = isBest ? '<span class="saif-instructor__rec-best" title="Best take for this lesson">Best take</span>' : '';
    var submittedLabel = rec.submittedAt ? 'Submitted ' + relativeTime(rec.submittedAt) : '';
    var pitchPoints = Array.isArray(rec.pitchData) && rec.pitchData.length ? rec.pitchData.length : 0;
    var hasReference = Array.isArray(rec.referencePitchData) && rec.referencePitchData.length > 0;

    var breakdown = '';
    if (rec.breakdown) {
      var rows = [];
      if (typeof rec.breakdown.pitch === 'number')       rows.push(['Pitch',       rec.breakdown.pitch]);
      if (typeof rec.breakdown.consistency === 'number') rows.push(['Consistency', rec.breakdown.consistency]);
      if (typeof rec.breakdown.steadiness === 'number')  rows.push(['Steadiness',  rec.breakdown.steadiness]);
      if (rows.length) {
        breakdown = '<div class="saif-instructor__rec-breakdown">' +
          rows.map(function (row) {
            return '<span><strong>' + row[1].toFixed(1) + '</strong>' + esc(row[0]) + '</span>';
          }).join('') +
        '</div>';
      }
    }

    // Legend sits under the canvas as accessible HTML; the Reference swatch
    // only appears when a reference contour is actually present.
    var graphLegend =
      '<div class="saif-instructor__rec-graph-legend">' +
        (hasReference
          ? '<span class="saif-instructor__rec-graph-legend-item">' +
              '<span class="saif-instructor__rec-graph-swatch saif-instructor__rec-graph-swatch--ref"></span>' +
              esc('Reference') +
            '</span>'
          : '') +
        '<span class="saif-instructor__rec-graph-legend-item">' +
          '<span class="saif-instructor__rec-graph-swatch saif-instructor__rec-graph-swatch--student"></span>' +
          esc('Student') +
        '</span>' +
      '</div>';
    var pitchGraph = pitchPoints
      ? '<div class="saif-instructor__rec-graph">' +
          '<canvas data-pitch-graph data-pitch-id="' + esc(rec.id) + '" width="640" height="120" aria-label="' + (hasReference ? 'Reference vs student pitch comparison graph' : 'Student pitch graph') + '"></canvas>' +
          graphLegend +
        '</div>'
      : '<div class="saif-instructor__rec-graph saif-instructor__rec-graph--empty">No pitch data captured for this recording.</div>';

    var existingFeedback = rec.feedback
      ? '<div class="saif-instructor__feedback-existing">' +
          '<strong>Your previous feedback (' + esc(rec.feedback.from || '') + '):</strong>' +
          '<p>' + esc(rec.feedback.text || '') + '</p>' +
          '<small>' + (rec.feedback.createdAt ? esc(new Date(rec.feedback.createdAt).toLocaleString()) : '') + '</small>' +
        '</div>'
      : '';

    var canned = QUICK_REPLIES.map(function (q) {
      return '<button type="button" class="saif-instructor__chip saif-instructor__chip--small" data-canned="' + esc(q) + '">' + esc(q) + '</button>';
    }).join('');

    // Stash pitch data on a hidden script tag — keeps the DOM cheap to query.
    // pitchData is student-controlled: escape every "<" as the JSON-safe
    // sequence backslash-u003c so a string containing "</script" can't
    // terminate the tag and inject markup into the instructor's session.
    var pitchPayload = '';
    if (pitchPoints) {
      try {
        pitchPayload = '<script type="application/json" data-pitch-data="' + esc(rec.id) + '">' +
          JSON.stringify(rec.pitchData).replace(/</g, '\\u003c') +
        '</script>';
      } catch (e) { pitchPayload = ''; }
    }
    // referencePitchData is also student-controlled (derived from the take's
    // captured reference) — same < escaping so a "</script" payload can't
    // break out of the tag. Only emitted when a non-empty reference exists.
    if (hasReference) {
      try {
        pitchPayload += '<script type="application/json" data-ref-pitch-data="' + esc(rec.id) + '">' +
          JSON.stringify(rec.referencePitchData).replace(/</g, '\\u003c') +
        '</script>';
      } catch (e) { /* leave student payload intact */ }
    }

    return '<article class="saif-instructor__rec-card' + (isBest ? ' saif-instructor__rec-card--best' : '') + '">' +
      pitchPayload +
      '<header class="saif-instructor__rec-header">' +
        '<div>' +
          '<div class="saif-instructor__rec-clip">' + esc(clipLabel) + bestBadge + '</div>' +
          '<div class="saif-instructor__rec-date">' + esc(dateLabel) + (duration ? ' · ' + esc(duration) : '') + '</div>' +
          (submittedLabel ? '<div class="saif-instructor__rec-submitted">' + esc(submittedLabel) + '</div>' : '') +
        '</div>' +
        (score ? '<div class="saif-instructor__rec-score ' + scoreClass + '">' + score + '</div>' : '') +
      '</header>' +
      breakdown +
      pitchGraph +
      '<div class="saif-instructor__audio-pair">' +
        (rec.referenceUrl ? (
          '<div class="saif-instructor__audio-col">' +
            '<span class="saif-instructor__audio-label">Reference</span>' +
            '<audio class="saif-instructor__rec-audio" controls preload="none" src="' + esc(rec.referenceUrl) + '"></audio>' +
          '</div>'
        ) : '') +
        '<div class="saif-instructor__audio-col">' +
          '<span class="saif-instructor__audio-label">Student recording</span>' +
          '<audio class="saif-instructor__rec-audio" controls preload="none" src="' + esc(rec.url) + '"></audio>' +
        '</div>' +
      '</div>' +
      existingFeedback +
      (rec.studentNote ? (
        '<div class="saif-instructor__student-note">' +
          '<span class="saif-instructor__student-note-label">Student\'s note</span>' +
          '<p class="saif-instructor__student-note-text">' + esc(rec.studentNote) + '</p>' +
        '</div>'
      ) : '') +
      '<form class="saif-instructor__feedback" data-feedback-form data-rec-id="' + esc(rec.id) + '">' +
        '<label class="visually-hidden" for="fb-' + esc(rec.id) + '">Feedback</label>' +
        '<textarea id="fb-' + esc(rec.id) + '" rows="3" placeholder="' + (rec.feedback ? 'Add an updated note...' : 'Type feedback, record a voice note, or both...') + '"></textarea>' +
        '<div class="saif-instructor__feedback-canned">' + canned + '</div>' +
        '<div class="saif-instructor__feedback-voice">' +
          '<button type="button" class="saif-instructor__voice-btn" data-voice-record>🎙 Record voice note</button>' +
          '<span class="saif-instructor__voice-timer" data-voice-timer hidden>0:00</span>' +
          '<audio controls data-voice-preview hidden></audio>' +
          '<button type="button" class="saif-instructor__voice-discard" data-voice-discard hidden aria-label="Discard voice note">✕ discard</button>' +
        '</div>' +
        '<div class="saif-instructor__feedback-row">' +
          '<button type="submit">' + (rec.feedback ? 'Update feedback' : 'Send feedback') + '</button>' +
          (rec.feedback ? '' : '<button type="button" class="saif-instructor__btn--ghost saif-instructor__quick-approve" data-quick-approve>&#10003; Solid — quick approval</button>') +
          '<span data-feedback-msg class="saif-instructor__feedback-msg"></span>' +
        '</div>' +
      '</form>' +
    '</article>';
  }

  /* ---- pitch graph painting ---- */
  function paintAllPitchGraphs(root) {
    root.querySelectorAll('[data-pitch-graph]').forEach(function (canvas) {
      var id = canvas.getAttribute('data-pitch-id');
      var dataNode = root.querySelector('script[data-pitch-data="' + id + '"]');
      if (!dataNode) return;
      var data, refData = null;
      try { data = JSON.parse(dataNode.textContent); } catch (e) { return; }
      var refNode = root.querySelector('script[data-ref-pitch-data="' + id + '"]');
      if (refNode) {
        try { refData = JSON.parse(refNode.textContent); } catch (e) { refData = null; }
      }
      drawComparisonGraph(canvas, data, refData);
    });
  }

  // Normalize a raw pitch array into clean { t, f } points.
  // Recordings save { t, f }; older takes may have { time, freq }.
  function normalizePitch(data) {
    if (!Array.isArray(data) || !data.length) return [];
    return data.map(function (p) {
      return { t: p.t != null ? p.t : p.time, f: p.f != null ? p.f : p.freq };
    }).filter(function (p) { return p.f && p.t != null; });
  }

  function drawComparisonGraph(canvas, studentData, refData) {
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    var w = canvas.width, h = canvas.height;

    var studentPts = normalizePitch(studentData);
    var refPts = normalizePitch(refData);
    // Student is the primary gate — without it there's nothing to compare.
    if (!studentPts.length && !refPts.length) return;

    // X axis = time over the UNION of both contours' ranges.
    var allPts = studentPts.concat(refPts);
    var tMin = Infinity, tMax = -Infinity;
    allPts.forEach(function (p) {
      if (p.t < tMin) tMin = p.t;
      if (p.t > tMax) tMax = p.t;
    });
    if (!isFinite(tMin) || !isFinite(tMax)) return;
    if (tMax === tMin) tMax = tMin + 1;

    // Y axis = pitch in MIDI across BOTH contours.
    var midiOf = function (f) { return 69 + 12 * Math.log2(f / 440); };
    var mMin = Infinity, mMax = -Infinity;
    allPts.forEach(function (p) {
      var m = midiOf(p.f);
      if (m < mMin) mMin = m;
      if (m > mMax) mMax = m;
    });
    if (!isFinite(mMin) || !isFinite(mMax)) return;
    if (mMax - mMin < 4) { var mid = (mMax + mMin) / 2; mMin = mid - 2; mMax = mid + 2; }
    var mSpan = (mMax - mMin) || 1; // guard divide-by-zero

    ctx.clearRect(0, 0, w, h);

    // background grid (gold-tinted) — unchanged
    ctx.strokeStyle = 'rgba(184,134,11,0.10)';
    ctx.lineWidth = 1;
    for (var g = 1; g < 4; g++) {
      var gy = h * g / 4;
      ctx.beginPath(); ctx.moveTo(0, gy); ctx.lineTo(w, gy); ctx.stroke();
    }

    var xOf = function (t) { return (t - tMin) / (tMax - tMin) * w; };
    var yOf = function (f) { return h - ((midiOf(f) - mMin) / mSpan) * h; };

    function tracePath(pts) {
      pts.forEach(function (p, i) {
        var x = xOf(p.t), y = yOf(p.f);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
    }

    // 1) REFERENCE contour first (underneath) — gold, with a light fill.
    if (refPts.length) {
      var refGrad = ctx.createLinearGradient(0, 0, 0, h);
      refGrad.addColorStop(0, 'rgba(184,134,11,0.18)');
      refGrad.addColorStop(1, 'rgba(184,134,11,0)');
      ctx.beginPath();
      tracePath(refPts);
      ctx.lineTo(xOf(refPts[refPts.length - 1].t), h);
      ctx.lineTo(xOf(refPts[0].t), h);
      ctx.closePath();
      ctx.fillStyle = refGrad;
      ctx.fill();

      ctx.beginPath();
      tracePath(refPts);
      ctx.strokeStyle = 'rgba(184,134,11,0.85)';
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.stroke();
    }

    // 2) STUDENT contour on TOP — teal, faint fill, must read over the reference.
    if (studentPts.length) {
      var stuGrad = ctx.createLinearGradient(0, 0, 0, h);
      stuGrad.addColorStop(0, 'rgba(45,106,90,0.16)');
      stuGrad.addColorStop(1, 'rgba(45,106,90,0)');
      ctx.beginPath();
      tracePath(studentPts);
      ctx.lineTo(xOf(studentPts[studentPts.length - 1].t), h);
      ctx.lineTo(xOf(studentPts[0].t), h);
      ctx.closePath();
      ctx.fillStyle = stuGrad;
      ctx.fill();

      ctx.beginPath();
      tracePath(studentPts);
      ctx.strokeStyle = '#2D6A5A';
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.stroke();
    }
  }

  /* -------------------------------------------------- helpers */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c];
    });
  }
  function relativeTime(iso) {
    var d = new Date(iso);
    var diff = Date.now() - d.getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm ago';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h ago';
    if (diff < 86400000 * 7) return Math.floor(diff / 86400000) + 'd ago';
    return d.toLocaleDateString();
  }
  function humanizeLessonId(id) {
    if (!id || id === '__free') return 'Free practice';
    // Lesson ids look like "course-handle::lesson-handle"
    var parts = String(id).split('::');
    var base = parts[parts.length - 1] || id;
    return base.replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }
  function badgeLabel(b) {
    return String(b).replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  /* -------------------------------------------------- events */
  document.querySelectorAll('[data-list-filter]').forEach(function (chip) {
    chip.addEventListener('click', function () {
      listFilter = chip.getAttribute('data-list-filter');
      document.querySelectorAll('[data-list-filter]').forEach(function (c) {
        c.classList.toggle('saif-instructor__chip--active', c === chip);
      });
      renderList();
    });
  });
  if (searchEl) searchEl.addEventListener('input', renderList);
  if (sortEl)   sortEl.addEventListener('change', renderList);

  listEl.addEventListener('click', function (e) {
    var item = e.target.closest('[data-customer-id]');
    if (!item) return;
    loadDetail(item.getAttribute('data-customer-id'));
  });
  listEl.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var item = e.target.closest('[data-customer-id]');
    if (!item) return;
    e.preventDefault();
    loadDetail(item.getAttribute('data-customer-id'));
  });

  /* -------------------------------------------------- tabs */
  var tabs = Array.prototype.slice.call(document.querySelectorAll('.saif-instructor__tab'));
  var panels = document.querySelectorAll('.saif-instructor__panel');
  function activateTab(tab) {
    var name = tab.getAttribute('data-tab');
    tabs.forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('saif-instructor__tab--active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      // Roving tabindex — only the active tab sits in the tab order.
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
    panels.forEach(function (p) { p.hidden = (p.getAttribute('data-panel') !== name); });
    if (name === 'bookings' && !bookingsLoaded) { loadBookings(); bookingsLoaded = true; }
    if (name === 'feed'     && !feedLoaded)     { loadFeed();     if (allStudents.length) feedLoaded = true; }
    document.dispatchEvent(new CustomEvent('saif:legacy-tab',{detail:{name:name}}));
  }
  tabs.forEach(function (tab, i) {
    tab.addEventListener('click', function () { activateTab(tab); });
    // Arrow-key navigation (WAI-ARIA tabs pattern): Left/Right cycle,
    // Home/End jump; focus moves with activation.
    tab.addEventListener('keydown', function (e) {
      var visibleTabs = tabs.filter(function(t){return !t.hidden;}), visibleIndex = visibleTabs.indexOf(tab);
      var next = null;
      if (e.key === 'ArrowRight') next = visibleTabs[(visibleIndex + 1) % visibleTabs.length];
      else if (e.key === 'ArrowLeft') next = visibleTabs[(visibleIndex - 1 + visibleTabs.length) % visibleTabs.length];
      else if (e.key === 'Home') next = visibleTabs[0];
      else if (e.key === 'End') next = visibleTabs[visibleTabs.length - 1];
      if (!next || next === tab) return;
      e.preventDefault();
      next.focus();
      activateTab(next);
    });
  });

  /* -------------------------------------------------- bookings */
  var bookingsListEl = document.getElementById('SaifInstructorBookings');
  var bookingsStatusEl = document.getElementById('SaifBookingsStatus');
  document.querySelectorAll('[data-bk-filter]').forEach(function (chip) {
    chip.addEventListener('click', function () {
      bookingFilter = chip.getAttribute('data-bk-filter');
      document.querySelectorAll('[data-bk-filter]').forEach(function (c) {
        c.classList.toggle('saif-instructor__chip--active', c === chip);
      });
      renderBookings();
    });
  });

  function loadBookings() {
    if (!bookingsListEl) return;
    if (bookingsStatusEl) bookingsStatusEl.textContent = 'Loading bookings...';
    window.SaifSync.instructor.listBookings().then(function (r) {
      if (!r || !r.ok) {
        if (bookingsStatusEl) bookingsStatusEl.textContent = 'Could not load bookings (' + (r && r.error || 'unknown') + ')';
        return;
      }
      allBookings = (r.bookings || []).map(function(b){ b.manageSupported = r.manageSupported === true; return b; });
      bookingIssues = (r.issues || []).filter(function (issue) { return issue.state === 'needs_review' || !!issue.last_error; });
      renderOverview();
      renderBookings();
    });
  }

  function renderBookings() {
    if (!bookingsListEl) return;
    var nowMs = Date.now();
    var items = allBookings.slice();
    if (bookingFilter === 'upcoming') {
      items = items.filter(function (b) {
        return b.status !== 'cancelled' && new Date(b.end || b.start).getTime() > nowMs;
      });
    } else if (bookingFilter === 'reschedule') {
      items = items.filter(function (b) { return b.status === 'reschedule_requested' || !!b.rescheduleRequest; });
    }

    if (bookingsStatusEl) {
      var label = bookingFilter === 'upcoming' ? 'upcoming session' :
                  bookingFilter === 'reschedule' ? 'reschedule request' : 'booking';
      bookingsStatusEl.textContent = items.length
        ? items.length + ' ' + label + (items.length === 1 ? '' : 's')
        : 'Nothing in this view.';
    }
    bookingsListEl.innerHTML = (bookingIssues.length ? '<div class="saif-instructor__booking-resched" role="status"><strong>' + bookingIssues.length + ' booking update' + (bookingIssues.length === 1 ? ' needs attention' : 's need attention') + '</strong><p>Some calendar or email updates have not finished. Automatic retries are running; items marked for review need an owner check.</p>' + bookingIssues.map(function (x) { return '<p>' + esc(x.orderId ? 'Order ' + x.orderId : x.session_id || 'Booking confirmation') + ' · ' + (x.state === 'needs_review' ? 'Owner review needed' : 'Retrying') + '</p>'; }).join('') + '</div>' : '') + items.map(renderBooking).join('');
    bookingsListEl.querySelectorAll('[data-booking-action]').forEach(function(btn){ btn.addEventListener('click',function(){ openBookingAction(btn.getAttribute('data-booking-id'),btn.getAttribute('data-booking-action')); }); });

    bookingsListEl.querySelectorAll('[data-jump-customer]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var cid = btn.getAttribute('data-jump-customer');
        // Switch to Students tab + open that customer
        document.querySelector('.saif-instructor__tab[data-tab="students"]').click();
        loadDetail(cid);
      });
    });
  }

  function rescheduleOpen(b) {var p=b.reschedulePolicy;return !!(p&&p.allowed&&Date.now()<=Date.parse(p.cutoffAt));}
  function weekLabel(p) {var fmt=new Intl.DateTimeFormat(undefined,{month:'short',day:'numeric',timeZone:'UTC'});return fmt.format(new Date(p.startDate+'T12:00:00Z'))+' – '+fmt.format(new Date(p.lastDate+'T12:00:00Z'));}
  function renderBooking(b) {
    var when = b.start ? new Date(b.start) : null;
    var dateLabel = when
      ? when.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) +
        ' · ' + when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
      : '';
    var statusBadge = '';
    if (b.status === 'reschedule_requested') {
      statusBadge = '<span class="saif-instructor__booking-badge saif-instructor__booking-badge--pending">Reschedule requested</span>';
    } else if (b.status === 'cancelled') {
      statusBadge = '<span class="saif-instructor__booking-badge saif-instructor__booking-badge--cancelled">Cancelled</span>';
    }
    if (['confirming','rescheduling','cancelling'].indexOf(b.status) !== -1) statusBadge = '<span class="saif-instructor__booking-badge saif-instructor__booking-badge--pending">' + ({confirming:'Preparing meeting',rescheduling:'Updating time',cancelling:'Cancelling session'})[b.status] + '</span>';
    var join = /^https:\/\/meet\.google\.com\//.test(b.joinUrl || '') && b.status === 'confirmed' ? '<a class="saif-instructor__btn" href="' + esc(b.joinUrl) + '" target="_blank" rel="noopener">Join on Google Meet</a>' : '';
    var manage = b.status === 'confirmed' && b.manageSupported ? (rescheduleOpen(b)?'<button type="button" class="saif-instructor__btn saif-instructor__btn--ghost" data-booking-id="' + esc(b.id) + '" data-booking-action="reschedule">Change time</button>':'<span>'+ (b.reschedulePolicy&&b.reschedulePolicy.reason!=='RESCHEDULE_ANCHOR_UNAVAILABLE'?'Rescheduling closed · 24-hour notice required.':'Rescheduling needs an academy review.')+'</span>')+'<button type="button" class="saif-instructor__btn saif-instructor__btn--ghost" data-booking-id="' + esc(b.id) + '" data-booking-action="cancel">Cancel session</button>' : '';
    var rr = b.rescheduleRequest;
    var rrBlock = rr
      ? '<div class="saif-instructor__booking-resched">' +
          '<strong>Reschedule request</strong>' +
          (rr.requestedDate || rr.requestedTime ? '<div>Preferred: ' + esc(rr.requestedDate || '') + ' ' + esc(rr.requestedTime || '') + '</div>' : '') +
          '<div>Reason: ' + esc(rr.reason || '') + '</div>' +
          '<small>Submitted ' + (rr.requestedAt ? esc(new Date(rr.requestedAt).toLocaleString()) : '') + '</small>' +
        '</div>'
      : '';

    return '<article class="saif-instructor__booking-card">' +
      '<header class="saif-instructor__booking-header">' +
        '<div class="saif-instructor__booking-when">' + esc(dateLabel) + statusBadge + '</div>' +
        '<div class="saif-instructor__booking-customer">' + esc(b.customerName || b.customerEmail) + '</div>' +
      '</header>' +
      '<div class="saif-instructor__booking-meta">' +
        '<span>' + esc(b.service || '') + '</span>' +
        '<span>' + (b.duration || 0) + ' min</span>' +
        '' +
      '</div>' +
      rrBlock +
      '<div class="saif-instructor__booking-actions">' + join + manage +
        '<button type="button" class="saif-instructor__btn saif-instructor__btn--ghost" data-jump-customer="' + esc(b.customerId) + '">Open student practice</button>' +
        '<a class="saif-instructor__btn saif-instructor__btn--ghost" href="mailto:' + esc(b.customerEmail || '') + '">Email student</a>' +
      '</div>' +
    '</article>';
  }

  function openBookingAction(id, action) {
    var booking = allBookings.find(function(b){return b.id === id;});
    if (!booking) return;
    if(action==='reschedule'&&!rescheduleOpen(booking)){loadBookings();return;}
    var dialog = document.createElement('dialog');
    dialog.className = 'saif-original-booking-dialog';
    dialog.style.cssText = 'max-width:560px;width:calc(100% - 32px);padding:24px;border:1px solid #d9d1c2;border-radius:16px;background:#faf7f2;color:#1a1814';
    dialog.setAttribute('aria-labelledby','OriginalBookingActionTitle');
    var opener=document.activeElement;
    dialog.innerHTML = '<form><h2 id="OriginalBookingActionTitle">' + (action === 'reschedule' ? 'Change session time' : 'Cancel session') + '</h2><p>' + esc(booking.customerName || booking.customerEmail) + '</p>' +
      (action === 'reschedule' ? '<p>Choose an agreed time from ' + esc(weekLabel(booking.reschedulePolicy)) + '. Toronto time. Changes close 24\u00a0hours before the session.</p><p><label for="OriginalBookingDate">New date</label><input style="display:block;width:100%;min-height:44px" type="date" id="OriginalBookingDate" name="date" required></p><p><label for="OriginalBookingTime">New time</label><input style="display:block;width:100%;min-height:44px" type="time" id="OriginalBookingTime" name="time" required></p>' : '<p>This cancels the session and sends a confirmation. Refunds are handled separately in Shopify.</p>') +
      '<label style="display:flex;gap:12px;align-items:center;min-height:44px"><input type="checkbox" name="agreed" required> I have agreed this change with the student.</label><p role="status" data-action-status></p><div style="display:flex;flex-wrap:wrap;gap:12px"><button class="saif-instructor__btn" type="submit">' + (action === 'reschedule' ? 'Save new time' : 'Cancel session') + '</button><button class="saif-instructor__btn saif-instructor__btn--ghost" type="button" data-close>Back</button></div></form>';
    document.body.appendChild(dialog);
    if(action==='reschedule'){var d=new Intl.DateTimeFormat('en-CA',{timeZone:'America/Toronto',year:'numeric',month:'2-digit',day:'2-digit'});dialog.querySelector('[name=date]').min=[d.format(new Date()),booking.reschedulePolicy.startDate].sort().at(-1);dialog.querySelector('[name=date]').max=booking.reschedulePolicy.lastDate;dialog.querySelector('[name=date]').value=d.format(new Date(booking.start));dialog.querySelector('[name=time]').value=new Intl.DateTimeFormat('en-GB',{timeZone:'America/Toronto',hour:'2-digit',minute:'2-digit',hourCycle:'h23'}).format(new Date(booking.start));}dialog.addEventListener('close',function(){dialog.remove();if(opener && opener.isConnected)opener.focus();});
    dialog.querySelector('[data-close]').onclick=function(){dialog.close();};
    dialog.querySelector('form').onsubmit=async function(event){
      event.preventDefault();var form=event.currentTarget,button=form.querySelector('[type="submit"]'),status=form.querySelector('[data-action-status]');button.disabled=true;
      try{
        var fd=new FormData(form),payload={bookingId:id,agreed:fd.get('agreed')==='on',newDate:fd.get('date'),newTime:fd.get('time')};
        var response=await fetch('/apps/saif/instructor/bookings/'+action,{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
        var result=await response.json();if(!response.ok||!result.ok)throw Error(result.error||'UPDATE_FAILED');
        dialog.close();loadBookings();
      }catch(error){status.textContent=({RESCHEDULE_CLOSED:'Rescheduling closes 24\u00a0hours before your lesson.',OUTSIDE_RESCHEDULE_WEEK:'Choose a date within this lesson’s original week.',RESCHEDULE_ANCHOR_UNAVAILABLE:'Please contact the academy to review this lesson’s dates.',})[error.message]||(error.message==='SLOT_CONFLICT'?'That time overlaps another booking. Choose another time.':error.message==='BOOKING_BUSY'?'This session is being updated. Please try again shortly.':'The change could not be saved. Check the time and try again.');button.disabled=false;}
    };
    dialog.showModal();dialog.querySelector('input,button').focus();
  }

  /* -------------------------------------------------- recent activity feed */
  var feedListEl   = document.getElementById('SaifInstructorFeed');
  var feedStatusEl = document.getElementById('SaifFeedStatus');

  function loadFeed() {
    if (!feedListEl) return;
    if (feedStatusEl) feedStatusEl.textContent = 'Pulling recent recordings across all students...';
    // Use the cache if students are already loaded; pull each student in series
    // (cheap because we stop after first ~25 needing feedback). Without a server
    // aggregation endpoint this is the simplest correct option.
    var queue = allStudents
      .filter(function (s) { return s.pendingFeedback > 0; })
      .sort(function (a, b) { return (b.latestRecordingAt || '').localeCompare(a.latestRecordingAt || ''); })
      .slice(0, 12);

    if (!queue.length) {
      if (feedStatusEl) feedStatusEl.textContent = 'You\'re caught up — no recordings awaiting feedback.';
      feedListEl.innerHTML = '';
      return;
    }

    Promise.all(queue.map(function (s) {
      return window.SaifSync.instructor.getStudent(s.customerId).then(function (r) {
        if (!r || !r.ok) return [];
        // SUBMITTED takes only (legacy null counts as submitted, explicit
        // false is a private practice take) that still lack feedback.
        var recs = (r.student.recordings || []).filter(function (rec) {
          return rec.submittedForFeedback !== false && !rec.feedback;
        });
        return recs.slice(0, 3).map(function (rec) {
          return { rec: rec, student: r.student };
        });
      });
    })).then(function (groups) {
      var flat = [].concat.apply([], groups);
      flat.sort(function (a, b) { return (b.rec.createdAt || '').localeCompare(a.rec.createdAt || ''); });
      if (feedStatusEl) {
        feedStatusEl.textContent = flat.length + ' recording' + (flat.length === 1 ? '' : 's') + ' awaiting feedback';
      }
      feedListEl.innerHTML = flat.map(function (item) {
        return '<article class="saif-instructor__feed-item" data-feed-rec="' + esc(item.rec.id) + '">' +
          '<div class="saif-instructor__feed-meta">' +
            '<button class="saif-instructor__feed-link" type="button" data-jump-customer="' + esc(item.student.customerId) + '">' +
              esc(item.student.name || item.student.email) +
            '</button>' +
            '<span>' + esc(relativeTime(item.rec.createdAt)) + '</span>' +
            (typeof item.rec.score === 'number' ? '<span>' + item.rec.score.toFixed(1) + '/10</span>' : '') +
          '</div>' +
          '<div class="saif-instructor__feed-clip">' + esc(item.rec.clipTitle || (item.rec.lessonId ? humanizeLessonId(item.rec.lessonId) : 'Free practice')) + '</div>' +
          (item.rec.studentNote
            ? '<p class="saif-instructor__feed-note"><strong>Their note:</strong> ' + esc(item.rec.studentNote) + '</p>' : '') +
          '<audio controls preload="none" src="' + esc(item.rec.url) + '"></audio>' +
          '<form class="saif-instructor__feedback saif-instructor__feedback--feed" data-feed-form data-cid="' + esc(item.student.customerId) + '" data-rid="' + esc(item.rec.id) + '">' +
            '<textarea rows="2" placeholder="Feedback for this take..." aria-label="Feedback"></textarea>' +
            '<div class="saif-instructor__feedback-voice">' +
              '<button type="button" class="saif-instructor__voice-btn" data-voice-record>🎙 Voice note</button>' +
              '<span class="saif-instructor__voice-timer" data-voice-timer hidden>0:00</span>' +
              '<audio controls data-voice-preview hidden></audio>' +
              '<button type="button" class="saif-instructor__voice-discard" data-voice-discard hidden aria-label="Discard voice note">✕ discard</button>' +
            '</div>' +
            '<div class="saif-instructor__feedback-row">' +
              '<button type="submit">Send feedback</button>' +
              '<button type="button" class="saif-instructor__btn--ghost saif-instructor__quick-approve" data-feed-approve>&#10003; Solid — quick approval</button>' +
              '<span data-feedback-msg class="saif-instructor__feedback-msg"></span>' +
            '</div>' +
          '</form>' +
        '</article>';
      }).join('');

      // Inline feedback straight from the queue — the whole point.
      feedListEl.querySelectorAll('[data-feed-form]').forEach(function (form) {
        wireVoiceRecorder(form);
        var textarea = form.querySelector('textarea');
        var msg = form.querySelector('[data-feedback-msg]');
        function send(text) {
          var btns = form.querySelectorAll('button');
          btns.forEach(function (b) { b.disabled = true; });
          var voiceBlob = form._voiceBlob || null;
          var doSend = function (audio) {
            return window.SaifSync.instructor.leaveFeedback(
              form.getAttribute('data-cid'), form.getAttribute('data-rid'), text, audio);
          };
          var p = voiceBlob
            ? new Promise(function (res, rej) {
                var fr = new FileReader();
                fr.onload = function () { res(doSend({ base64: String(fr.result).split(',')[1] || '', mimeType: voiceBlob.type || 'audio/webm' })); };
                fr.onerror = rej;
                fr.readAsDataURL(voiceBlob);
              })
            : doSend(null);
          p.then(function (r) {
            if (r && r.ok) {
              var art = form.closest('.saif-instructor__feed-item');
              if (art) art.remove();
              var left = feedListEl.querySelectorAll('.saif-instructor__feed-item').length;
              if (feedStatusEl) feedStatusEl.textContent = left
                ? left + ' recording' + (left === 1 ? '' : 's') + ' awaiting feedback'
                : "You're caught up — no recordings awaiting feedback.";
              detailCache[form.getAttribute('data-cid')] = null; // detail view refetches fresh
            } else {
              btns.forEach(function (b) { b.disabled = false; });
              if (msg) msg.textContent = 'Failed: ' + ((r && r.error) || 'unknown');
            }
          });
        }
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          var text = (textarea.value || '').trim();
          if (!text && !form._voiceBlob) { if (msg) msg.textContent = 'Type feedback or record a voice note.'; return; }
          send(text);
        });
        var approve = form.querySelector('[data-feed-approve]');
        if (approve) approve.addEventListener('click', function () {
          send("Reviewed your take — it's solid. Keep going exactly like this; nothing to correct here.");
        });
      });

      feedListEl.querySelectorAll('[data-jump-customer]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var cid = btn.getAttribute('data-jump-customer');
          document.querySelector('.saif-instructor__tab[data-tab="students"]').click();
          loadDetail(cid);
        });
      });
    });
  }

  /* -------------------------------------------------- boot */
  loadList();
})();
